import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Lock, CreditCard } from 'lucide-react';
import { getDoctorAccess, hasControl } from '../redux/subscriptionSlice';
import { ACCESS_CONTROLS } from '../data/accessControls';

// The control's name in the reader's language: featureLock.controls.<id> in
// the locales, the (English) ACCESS_CONTROLS label when a new control has no
// entry yet. data/accessControls.js stays English — it is byte-mirrored with
// the backend and the Super Admin copy (mirroredFiles.test.js).
const labelOf = (t, id) => {
  const fallback = ACCESS_CONTROLS.find((c) => c.id === id)?.label || t('featureLock.this_feature');
  return t(`featureLock.controls.${id}`, { defaultValue: fallback });
};

// Route guard: blocks a DOCTOR from a page their plan doesn't include (e.g. patient
// library / online payment / chat). Non-doctors (admin/patient) pass through. It
// refetches the doctor's resolved access on mount and waits for it, so it never
// false-blocks a doctor who actually has the feature.
export default function FeatureGuard({ control }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const role = (localStorage.getItem('Role') || '').toLowerCase();
  const doctorId = localStorage.getItem('ID');
  const controls = useSelector((s) => s.subscription?.doctorAccess?.controls);
  const [ready, setReady] = useState(role !== 'doctor');

  useEffect(() => {
    if (role !== 'doctor' || !doctorId) { setReady(true); return; }
    Promise.resolve(dispatch(getDoctorAccess(doctorId))).finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (role !== 'doctor') return <Outlet />;

  if (!ready) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (hasControl(controls, control)) return <Outlet />;

  // Blocked — the feature isn't in the doctor's current plan.
  return (
    <div className="w-full flex items-center justify-center py-16 px-4" data-testid={`feature-locked-${control}`}>
      <div className="bg-white rounded-2xl shadow-md max-w-md w-full p-8 text-center">
        <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center">
          <Lock className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-bold text-[#1a365d] mb-2">
          {t('featureLock.plan_title', { feature: labelOf(t, control), interpolation: { escapeValue: false } })}
        </h2>
        <p className="text-sm text-slate-500 mb-6">{t('featureLock.plan_message')}</p>
        <button
          type="button"
          onClick={() => navigate('/mySubscription')}
          data-testid={`feature-locked-cta-${control}`}
          className="w-full px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg flex items-center justify-center gap-2"
        >
          <CreditCard className="w-4 h-4" /> {t('featureLock.plan_cta')}
        </button>
      </div>
    </div>
  );
}
